import React, { useState } from 'react';
import { Box, Button, Collapse, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DeleteAction, EditAction, VisibilityAction } from '../../../components/row-actions';

interface SectionCardProps {
    dragHandle: React.ReactNode;
    // Ícone do tipo da seção, exibido ao lado do título
    icon?: React.ReactNode;
    title: string;
    subtitle?: string;
    chips?: React.ReactNode;
    isVisible: boolean;
    canDelete: boolean;
    deleteDisabledReason?: string;
    itemCount?: number;
    itemNoun?: string;
    // Desabilita edição e visibilidade (ex.: home ainda no layout padrão)
    actionsDisabled?: boolean;
    onToggleVisibility: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onAddItem?: () => void;
    // Mockup visual exibido abaixo do cabeçalho (ex.: miniaturas de banners e notícias)
    preview?: React.ReactNode;
    children?: React.ReactNode;
}

export function SectionCard({
    dragHandle, icon, title, subtitle, chips, isVisible, canDelete, deleteDisabledReason, itemCount, itemNoun, actionsDisabled,
    onToggleVisibility, onEdit, onDelete, onAddItem, preview, children,
}: SectionCardProps) {
    const [expanded, setExpanded] = useState(false);
    const hasItems = !!onAddItem;

    return (
        <Paper variant="outlined" sx={{ borderLeft: '3px solid', borderLeftColor: isVisible ? 'primary.main' : 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, pr: 0.5 }}>
                {dragHandle}
                {icon && (
                    <Box
                        sx={{
                            display: 'flex', p: 0.75, borderRadius: 1, flexShrink: 0, opacity: isVisible ? 1 : 0.6,
                            bgcolor: 'action.selected', color: 'primary.main', '& svg': { fontSize: 20 },
                        }}
                    >
                        {icon}
                    </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0, opacity: isVisible ? 1 : 0.6 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>{title}</Typography>
                    {subtitle && <Typography variant="caption" color="text.secondary" noWrap component="div">{subtitle}</Typography>}
                </Box>
                {chips}
                {hasItems && (
                    <Button size="small" onClick={() => setExpanded(!expanded)} endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ flexShrink: 0 }}>
                        {itemCount ?? 0} {itemNoun ? `${itemNoun.toLowerCase()}(s)` : 'item(ns)'}
                    </Button>
                )}
                <VisibilityAction isVisible={isVisible} onClick={onToggleVisibility} disabled={actionsDisabled} />
                <EditAction onClick={onEdit} disabled={actionsDisabled} />
                <DeleteAction title={canDelete ? 'Excluir' : deleteDisabledReason || ''} disabled={!canDelete} onClick={onDelete} />
            </Box>
            {preview && (
                <Box sx={{ px: 1.5, pb: 1.5, pl: 6, opacity: isVisible ? 1 : 0.5 }}>
                    {preview}
                </Box>
            )}
            {hasItems && (
                <Collapse in={expanded} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                        {children}
                        <Button size="small" startIcon={<AddIcon />} onClick={onAddItem} sx={{ mt: 1 }}>
                            Adicionar {itemNoun?.toLowerCase() || 'item'}
                        </Button>
                    </Box>
                </Collapse>
            )}
        </Paper>
    );
}
